import {getItems, getItemById, createItem, editItem, deleteItem} from '@/store/main'
import {DEFAULT_PARAMS, DEFAULT_SIZE} from '@/helpers/table'
import {addFilter, createParams, isDuplicateFilter, updateFilter} from '@/helpers/filterHelper'
import axios from '@/plugins/axios'
import customErrors from '@/store/error'
import i18n from '@/i18n/i18n'
// import {translateAndSortTypeObj} from '@/helpers/translator'

const TYPE_LOADING = 'AUDIENCE'
const TYPE_LOADING_ACTION = 'ACTION'

const state = () => ({
  items: [],
  item: null,
  loading: false,
  actionLoading: false,
  dataPagination: {},
  params: {...DEFAULT_PARAMS},
  isShowFilterBar: false,
  selectedAppFilter: {},
  selectedTableFilters: [],
  isResetSortParams: false,
})

const getters = {
  permission: (state, getters, rootState) => {
    return {
      read: rootState.auth?.permissions?.some((a) => a === 'AUDIENCE_READ'),
      readAll: rootState.auth?.permissions?.some((a) => a === 'AUDIENCE_READ_ALL'),
      new: rootState.auth.permissions?.some((a) => a === 'AUDIENCE_CREATE'),
      newForClient: rootState.auth.permissions?.some((a) => a === 'AUDIENCE_CREATE_FOR_CLIENT'),
      update: rootState.auth?.permissions?.some((a) => a === 'AUDIENCE_UPDATE'),
      remove: rootState.auth?.permissions?.some((a) => a === 'AUDIENCE_REMOVE'),
    }
  },
}

const mutations = {
  SET_ITEMS(state, items) {
    state.items = items
    setTimeout(() => {
      state.isResetSortParams = false
    }, 0)
  },
  SET_ITEM_BY_ID(state, item) {
    state.item = item
  },
  SET_PAGINATION(state, data) {
    state.dataPagination = {
      pageNumber: data.number + 1,
      totalPages: data.totalPages,
    }
  },
  SET_PARAMS(state, params) {
    state.params = {
      ...state.params,
      ...params,
    }
  },
  DELETE_PARAM(state, param) {
    delete state.params[param.name]
  },
  SET_PAGE_NUMBER(state, {page = DEFAULT_PARAMS.page}) {
    state.params = {
      ...state.params,
      page: page - 1,
    }
  },
  SET_ROW_SIZE(state, size = DEFAULT_SIZE) {
    state.params = {
      ...state.params,
      size,
    }
  },
  SET_SORT_PARAMS(state, {sortNameParam, sortSetParam}) {
    const sortStr = sortNameParam && sortSetParam ? `${sortNameParam},${sortSetParam}` : DEFAULT_PARAMS.sort
    state.params = {
      ...state.params,
      sort: sortStr,
    }
  },
  SET_LOADING(state, {type, loading}) {
    switch (type) {
      case TYPE_LOADING:
        state.loading = loading
        break
      case TYPE_LOADING_ACTION:
        state.actionLoading = loading
        break
    }
  },
  RESET_PARAMS(state) {
    const size = state.params.size
    state.params = {...DEFAULT_PARAMS, size, page: 0}
    state.isShowFilterBar = false
    state.selectedAppFilter = {}
    state.selectedTableFilters = []
    state.isResetSortParams = true
  },
  SET_APP_FILTER(state, {data = {}, isApi = false}) {
    state.isShowFilterBar = true

    if (isApi) {
      state.selectedAppFilter = data
    } else {
      const {value, placeholder, filterType} = data
      state.selectedAppFilter = {
        name: value,
        value: '',
        placeholder: placeholder ?? value,
        type: filterType,
      }
    }
  },
  SET_TABLE_FILTERS(state, data = undefined) {
    const {name} = state.selectedAppFilter
    const filtersToUpdate = data ?? state.selectedTableFilters

    if (!isDuplicateFilter(state.selectedTableFilters, name)) {
      addFilter(state, filtersToUpdate, data)
    } else {
      updateFilter(filtersToUpdate, state.selectedAppFilter)
    }

    const params = createParams(state.selectedTableFilters)

    this.commit('audience/SET_PARAMS', params)
    this.commit('audience/SET_PAGE_NUMBER', {page: DEFAULT_PARAMS.page})
  },
  DELETE_TABLE_FILTERS(state, data) {
    state.selectedTableFilters = state.selectedTableFilters.filter((a) => a.name !== data.name)
    this.commit('audience/DELETE_PARAM', data)
    this.commit('audience/SET_PAGE_NUMBER', {page: DEFAULT_PARAMS.page})

    if (state.selectedTableFilters.length === 0) {
      state.isShowFilterBar = false
    } else {
      state.selectedAppFilter = state.selectedTableFilters[0]
    }
  },
}

const actions = {
  async GET_ITEMS({commit, dispatch}, params) {
    commit('SET_LOADING', {type: TYPE_LOADING, loading: true})
    commit('SET_ITEM_BY_ID', null)
    await getItems(commit, dispatch, TYPE_LOADING, 'audience/all', params)
  },
  async GET_ITEM_BY_ID({commit, dispatch}, id) {
    await getItemById(commit, dispatch, TYPE_LOADING, `audience/view/${id}`)
  },
  async CREATE_AUDIENCE({commit, dispatch}, formData) {
    const {userId, ...params} = formData
    const url = userId ? `audience/create/${userId}` : 'audience/create'
    return await createItem(commit, dispatch, TYPE_LOADING_ACTION, url, params)
  },
  async EDIT_AUDIENCE({commit, dispatch}, {id, formData}) {
    return await editItem(commit, dispatch, TYPE_LOADING_ACTION, `audience/update/${id}`, formData)
  },
  async DELETE_AUDIENCE({commit, dispatch}, id) {
    return await deleteItem(commit, dispatch, TYPE_LOADING_ACTION, `audience/remove/${id}`)
  },
  async DUPLICATE_AUDIENCE({commit, dispatch}, id) {
    commit('SET_LOADING', {type: TYPE_LOADING_ACTION, loading: true})
    try {
      const {status} = await axios.post(`audience/duplicate/${id}`)
      if (status === 200) {
        dispatch('SHOW_TOAST_SUCCESS', i18n.t('toast.successDuplicated'), {root: true})
        return true
      }
    } catch (err) {
      await customErrors(err, dispatch)
    } finally {
      commit('SET_LOADING', {type: TYPE_LOADING_ACTION, loading: false})
    }
  },
  async GET_PIXEL_CAMPAIGNS({dispatch}, userId) {
    try {
      const {data} = await axios.get(`dictionary/audience/pixel/campaigns/${userId}/read-all`)
      return data
    } catch (err) {
      await customErrors(err, dispatch)
      return []
    }
  },
  async GET_SEGMENT_PROVIDERS({dispatch}) {
    try {
      const {data} = await axios.get('dictionary/audience/segment/provider/read-all')
      return data
    } catch (err) {
      await customErrors(err, dispatch)
      return []
    }
  },
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
}
